
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/lib/types';
import { convertSupabaseUser } from './utils';
import { deleteUserAccount } from '@/lib/supabase/user';

export const login = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      return { success: false, message: error.message || 'Failed to login' };
    }
    
    return { success: true, message: 'Logged in successfully' };
  } catch (error: any) {
    return { success: false, message: error.message || 'An error occurred during login' };
  }
};

export const register = async (email: string, password: string, name: string) => {
  try {
    // Ensure signup confirmation links always return to this app (not localhost)
    const redirectUrl = `${window.location.origin}/auth`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          name,
        },
      },
    });

    if (error) {
      return { success: false, message: error.message || 'Failed to register' };
    }

    return { success: true, message: 'Registered successfully' };
  } catch (error: any) {
    return { success: false, message: error.message || 'An error occurred during registration' };
  }
};

export const logout = async () => {
  return await supabase.auth.signOut();
};

export const resetPasswordRequest = async (email: string) => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) {
      return { success: false, message: error.message || 'Failed to send reset link' };
    }
    
    return { success: true, message: 'Password reset link sent to your email' };
  } catch (error: any) {
    return { success: false, message: error.message || 'An error occurred during password reset' };
  }
};

export const changePassword = async (newPassword: string) => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    
    if (error) {
      return { success: false, message: error.message || 'Failed to change password' };
    }
    
    return { success: true, message: 'Password changed successfully' };
  } catch (error: any) {
    return { success: false, message: error.message || 'An error occurred during password change' };
  }
};

export const updateProfile = async (data: { name?: string; email?: string }) => {
  try {
    const { data: userData, error } = await supabase.auth.updateUser({
      email: data.email,
      data: { name: data.name },
    });
    
    if (error) {
      return { success: false, message: error.message || 'Failed to update profile', user: null };
    }
    
    const user = userData.user ? convertSupabaseUser(userData.user) : null;
    
    return { success: true, message: 'Profile updated successfully', user };
  } catch (error: any) {
    return { success: false, message: error.message || 'An error occurred during profile update', user: null };
  }
};

export const deleteAccount = async () => {
  try {
    const result = await deleteUserAccount();
    
    if (!result.success) {
      return { success: false, message: result.message || 'Failed to delete account' };
    }
    
    return { success: true, message: 'Your account has been deleted successfully' };
  } catch (error: any) {
    return { success: false, message: error.message || 'An error occurred during account deletion' };
  }
};
